import { z } from 'zod';
import { pageSchema, resourceKeySchema, statusToneSchema, timestampSchema } from './common';

/**
 * How a project divides its own work (database: `project_division`).
 *
 * Two axes, deliberately orthogonal:
 *
 *  - A **stage** is a lifecycle phase. Pre-planning, planning, construction,
 *    handover. Sequential, one broadly current at a time, and the axis a
 *    steering group reads progress along: "we are in construction" is a
 *    sentence about the whole undertaking.
 *
 *  - A **section** is a part of the undertaking -- the Finnish `hankeosa`. A
 *    station area, a track section, a discipline, a building. Many run at
 *    once, they do not follow one another, and it is the axis the people doing
 *    the work organize themselves along: "whose section is this?"
 *
 * A task carries one of each, and neither implies the other. That is what makes
 * the pair worth having: "planning work on the north section" is a cell in a
 * matrix that neither axis alone can name, and it is the cell most status
 * questions are actually about.
 *
 * ## Why this is not domain-specific
 *
 * Nothing here names an industry. A railway project's stages
 * (esisuunnittelu -> yleissuunnittelu -> rakentaminen) and a software
 * rollout's (discovery -> build -> rollout) are the same five columns holding
 * different strings, and the strings arrive from a template an administrator
 * picks and then edits. `STAGE_TEMPLATES` below ships two; adding a third is a
 * catalog entry and three translation keys, with no migration and no code.
 *
 * The alternative -- an enum of stage names in the schema -- would have made
 * every deployment share one industry's vocabulary, and the first client whose
 * process has six phases instead of five would have needed a migration to say
 * so.
 *
 * ## Why they are one kind of thing
 *
 * Structurally a stage and a section are the same row: a project-scoped,
 * ordered, named division with a status, an optional window and a colour. They
 * are one table and one contract for the reason `RegisterItem` gives for its
 * five kinds -- writing them separately means two migrations, two services, two
 * screens and two sets of translations to express a difference that amounts to
 * whether the order means "next" or just "below".
 */

/* --------------------------------------------------------------- the kinds */

export const PROJECT_DIVISION_KINDS = ['stage', 'section'] as const;
export const projectDivisionKindSchema = z.enum(PROJECT_DIVISION_KINDS);
export type ProjectDivisionKind = (typeof PROJECT_DIVISION_KINDS)[number];

export function isProjectDivisionKind(value: string): value is ProjectDivisionKind {
  return (PROJECT_DIVISION_KINDS as readonly string[]).includes(value);
}

/**
 * Where a division is in its own life.
 *
 * One vocabulary for both kinds. `on_hold` is the member that earns its place:
 * a suspended section is the thing a reader most needs to see, and a two-state
 * "done or not" flag cannot say it. A stage rarely uses it and loses nothing by
 * its being available.
 */
export const PROJECT_DIVISION_STATUSES = ['not_started', 'active', 'on_hold', 'complete'] as const;
export const projectDivisionStatusSchema = z.enum(PROJECT_DIVISION_STATUSES);
export type ProjectDivisionStatus = (typeof PROJECT_DIVISION_STATUSES)[number];

/** Statuses that mean work is still expected here. Drives the "open" counts. */
export const OPEN_PROJECT_DIVISION_STATUSES = [
  'not_started',
  'active',
  'on_hold',
] as const satisfies readonly ProjectDivisionStatus[];

export function isOpenProjectDivisionStatus(status: ProjectDivisionStatus): boolean {
  return (OPEN_PROJECT_DIVISION_STATUSES as readonly string[]).includes(status);
}

/**
 * The tone a division is drawn in when it has not been given one.
 *
 * Derived from status rather than stored, for the reason `registerItemTone`
 * exists: the list, the Gantt band and the dashboard tile must not be able to
 * disagree about the colour of the same phase.
 */
export function projectDivisionTone(division: {
  status: ProjectDivisionStatus;
  colorTone?: string | null;
}): z.infer<typeof statusToneSchema> {
  const explicit = division.colorTone;
  if (explicit && statusToneSchema.safeParse(explicit).success) {
    return statusToneSchema.parse(explicit);
  }
  switch (division.status) {
    case 'active':
      return 'info';
    case 'complete':
      return 'success';
    case 'on_hold':
      return 'warning';
    case 'not_started':
      return 'neutral';
  }
}

/* ------------------------------------------------------------- the record */

export const projectDivisionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  kind: projectDivisionKindSchema,
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  status: projectDivisionStatusSchema,
  /** Position within the kind. For a stage this is the lifecycle sequence. */
  orderIndex: z.number().int(),
  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),
  /** Explicit override; null means the tone is derived from `status`. */
  colorTone: statusToneSchema.nullable(),
  /** Resolved on the way out, so no client has to repeat the derivation. */
  tone: statusToneSchema,
  /** Leaf schedule tasks filed under this division. */
  taskCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdByUserId: z.string().uuid().nullable(),
  updatedByUserId: z.string().uuid().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ProjectDivision = z.infer<typeof projectDivisionSchema>;

export const projectDivisionPageSchema = pageSchema(projectDivisionSchema);

const divisionWritableFields = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(4000).nullish(),
  status: projectDivisionStatusSchema.default('not_started'),
  orderIndex: z.number().int().min(0).max(10_000).default(0),
  startsOn: z.string().date().nullish(),
  endsOn: z.string().date().nullish(),
  colorTone: statusToneSchema.nullish(),
});

/** A window whose end precedes its start is a typo, not a short phase. */
function refineWindow<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value) => {
      const record = value as { startsOn?: string | null; endsOn?: string | null };
      if (!record.startsOn || !record.endsOn) return true;
      return record.startsOn <= record.endsOn;
    },
    { message: 'endsOn must not precede startsOn', path: ['endsOn'] },
  );
}

export const createProjectDivisionSchema = refineWindow(
  divisionWritableFields
    .extend({
      kind: projectDivisionKindSchema,
      key: resourceKeySchema,
    })
    .strict(),
);
export type CreateProjectDivision = z.infer<typeof createProjectDivisionSchema>;

/**
 * `kind` and `key` are absent on purpose.
 *
 * `kind` is what a division *is*: moving a stage onto the section axis would
 * silently unfile every task pointing at it from the column that no longer
 * matches, and "delete it and make the other one" states the same intent
 * without the trap. `key` is what configuration and deep links carry, and a
 * renameable key is not a key -- `name` is the field that exists to be changed.
 */
export const updateProjectDivisionSchema = refineWindow(
  divisionWritableFields
    .partial()
    .extend({
      /** Value last read by the client; drift yields 412. */
      version: z.number().int().nonnegative(),
    })
    .strict(),
);
export type UpdateProjectDivision = z.infer<typeof updateProjectDivisionSchema>;

export const projectDivisionListQuerySchema = z.object({
  kind: projectDivisionKindSchema.optional(),
  status: projectDivisionStatusSchema.optional(),
});
export type ProjectDivisionListQuery = z.infer<typeof projectDivisionListQuerySchema>;

/**
 * Reordering, as one request rather than one PATCH per row.
 *
 * A drag that moves one stage renumbers every stage after it. Sent one at a
 * time that is N round trips, N audit events and N chances to be interrupted
 * half-way, leaving the lifecycle in an order nobody chose.
 */
export const reorderProjectDivisionsSchema = z
  .object({
    kind: projectDivisionKindSchema,
    /** Every division of this kind, in the order wanted. */
    orderedIds: z.array(z.string().uuid()).min(1).max(200),
  })
  .strict();
export type ReorderProjectDivisions = z.infer<typeof reorderProjectDivisionsSchema>;

/* ------------------------------------------------------------- templates */

/**
 * Starting sets of stages, so a new project is not a blank list.
 *
 * The names are translation keys rather than strings: a template is offered in
 * the reader's language, and what it writes is an ordinary row the project then
 * renames. Nothing downstream knows a template was involved -- this is a
 * convenience at creation time, not a type of project.
 *
 * Two ship. `infrastructure` follows how a built-asset project is actually
 * divided -- the design work is several distinct phases with different
 * approvals, and handover and warranty are real periods with obligations rather
 * than the moment construction stops. `generic` is the four-phase shape that
 * fits everything else, and is the right default when nobody has said.
 */
export interface StageTemplateEntry {
  key: string;
  nameKey: string;
}

export interface StageTemplate {
  key: string;
  nameKey: string;
  descriptionKey: string;
  stages: readonly StageTemplateEntry[];
}

export const STAGE_TEMPLATES = {
  infrastructure: {
    key: 'infrastructure',
    nameKey: 'structure.templates.infrastructure.name',
    descriptionKey: 'structure.templates.infrastructure.description',
    stages: [
      { key: 'pre-planning', nameKey: 'structure.stages.prePlanning' },
      { key: 'planning', nameKey: 'structure.stages.planning' },
      { key: 'design', nameKey: 'structure.stages.design' },
      { key: 'procurement', nameKey: 'structure.stages.procurement' },
      { key: 'construction', nameKey: 'structure.stages.construction' },
      { key: 'commissioning', nameKey: 'structure.stages.commissioning' },
      { key: 'handover', nameKey: 'structure.stages.handover' },
      { key: 'warranty', nameKey: 'structure.stages.warranty' },
    ],
  },
  generic: {
    key: 'generic',
    nameKey: 'structure.templates.generic.name',
    descriptionKey: 'structure.templates.generic.description',
    stages: [
      { key: 'initiation', nameKey: 'structure.stages.initiation' },
      { key: 'planning', nameKey: 'structure.stages.planning' },
      { key: 'execution', nameKey: 'structure.stages.execution' },
      { key: 'closure', nameKey: 'structure.stages.closure' },
    ],
  },
} as const satisfies Record<string, StageTemplate>;

export type StageTemplateKey = keyof typeof STAGE_TEMPLATES;
export const STAGE_TEMPLATE_KEYS = Object.keys(STAGE_TEMPLATES) as StageTemplateKey[];
export const stageTemplateKeySchema = z.enum(
  STAGE_TEMPLATE_KEYS as [StageTemplateKey, ...StageTemplateKey[]],
);

/**
 * Applying a template.
 *
 * Additive by construction: it creates the stages whose keys the project does
 * not already have and leaves every existing row untouched, so applying one
 * twice is a no-op and applying a second template over a project that has been
 * edited cannot silently discard the edits. There is no "replace" mode, because
 * the destructive version of this is `delete` on the rows the reader can see.
 */
export const applyStageTemplateSchema = z
  .object({
    template: stageTemplateKeySchema,
    /**
     * The stage names, resolved, keyed by the template's stage key.
     *
     * Sent by the caller rather than resolved server-side because the API has
     * no translation catalogue and the reader's language is a property of their
     * browser, not of the deployment -- the same division of labour every other
     * `nameKey` in the contracts follows. A key the caller omits falls back to
     * the stage key itself, so a missing translation produces a stage called
     * `commissioning` rather than a template that refuses to apply.
     */
    names: z.record(z.string(), z.string().min(1).max(200)).default({}),
  })
  .strict();
export type ApplyStageTemplate = z.infer<typeof applyStageTemplateSchema>;

/**
 * The per-division rollup the project page and the dashboard tile read.
 *
 * Its own shape rather than a page of tasks the browser groups, for the reason
 * `scheduleSummarySchema` gives: a tile that downloads every task in order to
 * count them gets slower as the project grows and is capped by the largest page
 * the API offers, so past that cap it is not merely slow, it is wrong.
 */
export const projectDivisionProgressSchema = z.object({
  divisionId: z.string().uuid(),
  kind: projectDivisionKindSchema,
  key: z.string(),
  name: z.string(),
  status: projectDivisionStatusSchema,
  tone: statusToneSchema,
  orderIndex: z.number().int(),
  startsOn: z.string().date().nullable(),
  endsOn: z.string().date().nullable(),
  /** Leaf tasks filed here. The denominator for the rest. */
  total: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  inProgress: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  /** Earliest calculated start and latest calculated finish across the leaves. */
  calculatedStart: z.string().date().nullable(),
  calculatedFinish: z.string().date().nullable(),
});
export type ProjectDivisionProgress = z.infer<typeof projectDivisionProgressSchema>;

export const projectStructureSummarySchema = z.object({
  projectId: z.string().uuid(),
  stages: z.array(projectDivisionProgressSchema),
  sections: z.array(projectDivisionProgressSchema),
  /** Leaf tasks filed under no division of that kind. */
  unassignedStageTasks: z.number().int().nonnegative(),
  unassignedSectionTasks: z.number().int().nonnegative(),
});
export type ProjectStructureSummary = z.infer<typeof projectStructureSummarySchema>;
